import { Controller } from "@hotwired/stimulus"

// Web shell sidebar (layouts/web): collapses to icons, remembered per browser.
// The state lives on <html data-web-rail> so the layout's head script can set
// it before first paint; this controller owns it after connect. Below 1200px
// the rail rests collapsed and expanding floats it over the page (not saved).
const STORAGE_KEY = "web_sidebar"
const NARROW_QUERY = "(max-width: 1199px)"

export default class extends Controller {
  static targets = ["toggle", "label", "scrim"]
  static values = { collapseLabel: String, expandLabel: String }

  connect() {
    this.media = window.matchMedia(NARROW_QUERY)
    this.onMediaChange = () => this.apply(this.restingState(), false)
    this.media.addEventListener("change", this.onMediaChange)
    this.apply(this.restingState(), false)
  }

  disconnect() {
    this.media.removeEventListener("change", this.onMediaChange)
  }

  toggle() {
    this.apply(this.collapsed ? "expanded" : "collapsed", true)
  }

  dismiss() {
    this.apply("collapsed", false)
  }

  navigate() {
    if (this.media.matches && !this.collapsed) this.apply("collapsed", false)
  }

  get collapsed() {
    return document.documentElement.dataset.webRail === "collapsed"
  }

  restingState() {
    return this.media.matches ? "collapsed" : this.read()
  }

  apply(state, remember) {
    document.documentElement.dataset.webRail = state
    if (remember && !this.media.matches) this.write(state)

    const collapsed = state === "collapsed"
    const label = collapsed ? this.expandLabelValue : this.collapseLabelValue
    this.labelTarget.textContent = label
    this.toggleTarget.setAttribute("aria-expanded", String(!collapsed))
    this.toggleTarget.setAttribute("aria-label", label)
    this.toggleTarget.title = label
    this.scrimTarget.hidden = !(this.media.matches && !collapsed)
  }

  read() {
    try {
      return localStorage.getItem(STORAGE_KEY) === "collapsed" ? "collapsed" : "expanded"
    } catch {
      return "expanded"
    }
  }

  write(state) {
    try {
      localStorage.setItem(STORAGE_KEY, state)
    } catch (error) {
      console.warn("Sidebar state not saved:", error)
    }
  }
}
